import {useContext, useEffect, useRef, useState} from "react";
import UserContext from "../../common/UserContext";
import {AssignedProductContext, GeneralContext} from "../FSARequestLicenseContext";
import {useLocalization} from "@progress/kendo-react-intl";
import axios from "axios";
import config from "../../../config";
import {PanelBar, PanelBarItem} from "@progress/kendo-react-layout";
import {RequestPanelBarHeader} from "./RequestPanelBarHeader";
import {RequestDownloadGrid} from "./RequestDownloadGrid";
import {Col, Row} from "reactstrap";
import Alert from "../../common/Alert";
import {
    cancelKey, confirmKey, errorOccurredRequestKey, fsaGenerationConfirmationKey,
    generateLicensesKey,
    mainMessages, notGeneratedAvailableRedeemKey,
    requestLicensesKey, totalCostIsKey
} from "../../../assets/text/MultilingualText";
import {Button} from "@progress/kendo-react-buttons";

// utilities
import {formatCurrency} from "../../common/utilities";

export const LicensePreview = (props) => {
    const {
        siteLanguageDefault,
        accessToken,
        timeout
    } = useContext(UserContext);

    const {
        setIsLoading,
        changeStepActivation,
        headerInfo
    } = useContext(GeneralContext);

    const {
        assignedProducts,
        setAssignedProducts,
    } = useContext(AssignedProductContext);

    const {
        setRequestedLicense,
        setRequestedProducts,
    } = props

    const localization = useLocalization();
    const [showConfirmation, setShowConfirmation] = useState(false)
    const [disableRequestLicense, setDisableRequestLicense] = useState(false)
    const [totalPrice, setTotalPrice] = useState(0)

    //default reset all host with checks
    useEffect(() => {
        let checkedAssignedProducts = [...assignedProducts]
        for (const host of checkedAssignedProducts) {
            host.icons.checked = true
        }
        setAssignedProducts(checkedAssignedProducts)
    }, [])

    //disable request license button if no hosts are checked
    useEffect(() => {
        let disabled = true
        for (const host of assignedProducts) {
            if (host.icons.checked) {
                disabled = false
            }
        }
        setDisableRequestLicense(disabled)

        if (showConfirmation && disabled) {
            setShowConfirmation(false)
        }
    }, [assignedProducts, showConfirmation])

    // Handles panel bar expanding based on whether icons have been clicked or not
    const iconClicked = useRef(false)
    const handleRequestSelect = (event) => {
        let hostIndex = event.target.props.title.props.index
        if (!iconClicked.current) {
            if (assignedProducts[hostIndex].expanded.length === 0) {
                assignedProducts[hostIndex].expanded = ['.0']
            } else {
                assignedProducts[hostIndex].expanded = []
            }
        } else {
            iconClicked.current = false
        }
        setAssignedProducts([...assignedProducts])
    };

    const [showError, setShowError] = useState(false)
    const alertHandler = () => {
        setShowError(false)
    }

    const checkInternalConnection = () => {
        let headers = {
            'Authorization': 'Bearer ' + accessToken,
        };
        let products = [];

        setIsLoading(true);
        axios.post(
            config.utilities.health,
            {headers: headers, timeout: timeout}
        )
            .then((response) => {
                for (const host of assignedProducts) {
                    if (host.icons.checked) {
                        let requestedHost = JSON.parse(JSON.stringify(host))
                        requestedHost.icons = {
                            checked: true,
                            loading: true,
                            download: false,
                            cloudAlert: false,
                            specialAlert: false,
                            supportError: false,
                            generationError: false,
                            qtyError: false,
                            entitlementError: false,
                        }
                        products.push(requestedHost)
                    }
                }
                setRequestedProducts(products)
                setRequestedLicense(true)
            })
            .catch((error) => {
                console.log("ERROR: Failed to internet check", error);
                if (!error.response) { //Internet disconnected
                    setShowError(true)
                    setIsLoading(false);
                } else {
                    setRequestedLicense(true)
                }
            })
    }

    useEffect(() => {
        let total = 0
        for (const host of assignedProducts) {
            if (host.icons.checked) {
                host.products.forEach((product) => {
                    total += product.total_cost
                })
            }
        }
        setTotalPrice(total)
    }, [assignedProducts])

    return (
        <>
            {assignedProducts.map((host, index) => {
                return (
                    <PanelBar
                        key={index}
                        isControlled={true}
                        expanded={host.expanded}
                        onSelect={handleRequestSelect}
                        className={'ksm-panelbar-default'}
                    >
                        <PanelBarItem
                            title={
                                <RequestPanelBarHeader
                                    index={index}
                                    iconClicked={iconClicked}
                                />
                            }
                        >
                            <RequestDownloadGrid
                                items={host.products}
                            />

                        </PanelBarItem>
                    </PanelBar>
                )
            })}

            <Row>
                <Col/>
                <Col>
                    {showConfirmation && (
                        <Alert
                            type={'warning'}
                            title={localization.toLanguageString(generateLicensesKey, mainMessages[siteLanguageDefault][generateLicensesKey])}
                            message={
                            <>
                                {localization.toLanguageString(totalCostIsKey, mainMessages[siteLanguageDefault][totalCostIsKey])}
                                {formatCurrency(
                                    totalPrice,
                                    headerInfo['currency_locale'],
                                    headerInfo['currency_code'])
                                }.&nbsp;
                                {localization.toLanguageString(fsaGenerationConfirmationKey, mainMessages[siteLanguageDefault][fsaGenerationConfirmationKey])}
                            </>
                        }
                        />
                    )}
                </Col>
                <Col>
                    <Button
                        style={{
                            float: 'right',
                        }}
                        themeColor={"primary"}
                        size={"large"}
                        fillMode={"solid"}
                        disabled={showConfirmation || disableRequestLicense}
                        onClick={() => {
                            setShowConfirmation(true)
                        }}
                    >
                        {localization.toLanguageString(requestLicensesKey, mainMessages[siteLanguageDefault][requestLicensesKey])}
                    </Button>
                </Col>
            </Row>

            {showConfirmation && (
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: '0.5rem'
                }}>
                    <Button
                        themeColor={"primary"}
                        size={"large"}
                        fillMode={"outline"}
                        onClick={() => {
                            changeStepActivation(1)
                        }}
                    >
                        {localization.toLanguageString(cancelKey, mainMessages[siteLanguageDefault][cancelKey])}
                    </Button>
                    <Button
                        themeColor={"primary"}
                        size={"large"}
                        fillMode={"solid"}
                        onClick={() => {
                            checkInternalConnection()
                        }}
                    >
                        {localization.toLanguageString(confirmKey, mainMessages[siteLanguageDefault][confirmKey])}
                    </Button>
                </div>
            )}

            {showError && (
                <divs style={{
                    display: 'flex',
                    justifyContent: 'center',
                }}>
                    <div style={{
                        maxWidth: '35.813rem',
                    }}>
                        <Alert
                            type={'error'}
                            showHandler={alertHandler}
                            title={localization.toLanguageString(errorOccurredRequestKey, mainMessages[siteLanguageDefault][errorOccurredRequestKey])}
                            message={localization.toLanguageString(notGeneratedAvailableRedeemKey, mainMessages[siteLanguageDefault][notGeneratedAvailableRedeemKey])}
                        />
                    </div>

                </divs>
            )}
        </>
    )
}